import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SonioxClient } from "@soniox/speech-to-text-web";
import "./recorder.css";
import micImg from "../../assets/mic.png";
import earImg from "../../assets/hear.png";

// Replace with your actual Soniox API key
const API_KEY =
  "a85657f407107e8424d76b89e7146a3e842424021c8d7a079ed291d8f537f2eb";

export default function RecorderPage() {
  const { state } = useLocation();
  const incident = state?.incident;
  const navigate = useNavigate();

  const [recording, setRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [fullTelugu, setFullTelugu] = useState("");
  const [partialTelugu, setPartialTelugu] = useState("");
  const [fullEnglish, setFullEnglish] = useState("");
  const [partialEnglish, setPartialEnglish] = useState("");

  const mediaRecorderRef = useRef(null);
  const sonioxClientRef = useRef(null);
  const streamRef = useRef(null);
  const audioChunks = useRef([]);
  const maxDurationTimerRef = useRef(null); // ✅ ADDED

  const bosChoice = state?.bosChoice;

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
          sampleRate: 16000,
        },
      });
      streamRef.current = stream;

      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: "audio/webm",
      });
      audioChunks.current = [];

      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunks.current.push(e.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunks.current, { type: "audio/webm" });
        setAudioURL(URL.createObjectURL(audioBlob));
      };

      sonioxClientRef.current = new SonioxClient({
        apiKey: API_KEY,
      });

      sonioxClientRef.current.start({
        model: "stt-rt-v3",
        languageHints: ["te"],
        translation: {
          type: "one_way",
          target_language: "en",
        },
        stream: stream,
        audioConstraints: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
        mediaRecorderOptions: {},
        onPartialResult: (result) => {
          const tokens = result.tokens || [];

          const teluguTokens = tokens.filter(
            (token) => token.translation_status === "original"
          );
          if (teluguTokens.length > 0) {
            const teluguText = teluguTokens.map((t) => t.text).join(" ");
            const hasFinalTelugu = teluguTokens.some((t) => t.is_final);
            if (hasFinalTelugu) {
              setFullTelugu((prev) => prev + teluguText.trim() + " ");
              setPartialTelugu("");
            } else {
              setPartialTelugu(teluguText);
            }
          }

          const englishTokens = tokens.filter(
            (token) => token.translation_status === "translation"
          );
          if (englishTokens.length > 0) {
            const englishText = englishTokens
              .map((t) => t.text || "")
              .join("")
              .replace(/\s+/g, " ")
              .trim();

            const hasFinalEnglish = englishTokens.some((t) => t.is_final);
            if (hasFinalEnglish && englishText) {
              setFullEnglish((prev) =>
                prev ? `${prev}${englishText}` : englishText
              );
              setPartialEnglish("");
            } else {
              setPartialEnglish(englishText);
            }
          }
        },
      });

      mediaRecorderRef.current.start();

      // ✅ AUTO STOP AFTER 30 SECONDS
      maxDurationTimerRef.current = setTimeout(() => {
        if (mediaRecorderRef.current?.state === "recording") {
          stopRecording();
        }
      }, 30000);

      setRecording(true);
      setAudioURL(null);
      setFullTelugu("");
      setPartialTelugu("");
      setFullEnglish("");
      setPartialEnglish("");
    } catch (error) {
      console.error("Error starting recording:", error);
    }
  };

  const stopRecording = () => {
    if (maxDurationTimerRef.current) {
      clearTimeout(maxDurationTimerRef.current);
      maxDurationTimerRef.current = null;
    }

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === "recording"
    ) {
      mediaRecorderRef.current.stop();
    }
    if (sonioxClientRef.current) {
      sonioxClientRef.current.stop();
    }
    setRecording(false);
  };

  const retakeRecording = () => {
    if (maxDurationTimerRef.current) {
      clearTimeout(maxDurationTimerRef.current);
      maxDurationTimerRef.current = null;
    }

    if (sonioxClientRef.current) {
      sonioxClientRef.current.cancel();
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setAudioURL(null);
    setRecording(false);
    setFullTelugu("");
    setPartialTelugu("");
    setFullEnglish("");
    setPartialEnglish("");
  };

  const submitRecording = async () => {
    try {
      const employeeCode = incident?.employeeCode;
      if (!employeeCode) throw new Error("Employee code not found");

      const userResponse = await fetch(
        `https://mondelezbc.vsmglobaltechnologies.com/api/user/details/${employeeCode}`
      );
      const userData = await userResponse.json();
      const username = userData.data?.name || "";

      const payload = {
        employeeCode,
        username,
        type: "incident",
        message: fullEnglish.trim(),
        choice: bosChoice || "",
        incidentName: incident?.incidentNameEnglish || "",
        incidentId: incident?._id || "",
      };

      await fetch(
        "https://mondelezbc.vsmglobaltechnologies.com/api/incident-audio",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      navigate("/thank-you");
    } catch (error) {
      console.error(error);
      alert("Failed to submit recording. Please try again.");
    }
  };

  useEffect(() => {
    return () => {
      if (maxDurationTimerRef.current) {
        clearTimeout(maxDurationTimerRef.current);
      }
      if (sonioxClientRef.current) {
        sonioxClientRef.current.cancel();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (audioURL) {
        URL.revokeObjectURL(audioURL);
      }
    };
  }, [audioURL]);

  return (
    <div className="recorder-page">
      <a className="home-icon" href="/">
        <svg
          fill="#fff"
          version="1.1"
          id="Capa_1"
          xmlns="http://www.w3.org/2000/svg"
          width="35px"
          height="35px"
          viewBox="0 0 495.398 495.398"
        >
          <g>
            <g>
              <g>
                <path
                  d="M487.083,225.514l-75.08-75.08V63.704c0-15.682-12.708-28.391-28.413-28.391c-15.669,0-28.377,12.709-28.377,28.391
				v29.941L299.31,37.74c-27.639-27.624-75.694-27.575-103.27,0.05L8.312,225.514c-11.082,11.104-11.082,29.071,0,40.158
				c11.087,11.101,29.089,11.101,40.172,0l187.71-187.729c6.115-6.083,16.893-6.083,22.976-0.018l187.742,187.747
				c5.567,5.551,12.825,8.312,20.081,8.312c7.271,0,14.541-2.764,20.091-8.312C498.17,254.586,498.17,236.619,487.083,225.514z"
                />
                <path
                  d="M257.561,131.836c-5.454-5.451-14.285-5.451-19.723,0L72.712,296.913c-2.607,2.606-4.085,6.164-4.085,9.877v120.401
				c0,28.253,22.908,51.16,51.16,51.16h81.754v-126.61h92.299v126.61h81.755c28.251,0,51.159-22.907,51.159-51.159V306.79
				c0-3.713-1.465-7.271-4.085-9.877L257.561,131.836z"
                />
              </g>
            </g>
          </g>
        </svg>
      </a>
      {incident && (
        <div className="incident-info">
          {bosChoice && <h2>{bosChoice}</h2>}
          <div className="incident-img-box">
            <img
              src={incident.filePathUrl}
              alt={incident.incidentNameEnglish}
              className="incident-img"
            />
          </div>
          <p>{incident.incidentNameEnglish}</p>
        </div>
      )}

      {!recording && !audioURL && (
        <div className="stage">
          <img src={micImg} alt="Mic" className="stage-icon" />
          <h2>వాయిస్ ద్వారా నివేదించండి</h2>
          <p>Report by voice</p>
          <button className="record-btn" onClick={startRecording}>
            Start
          </button>
        </div>
      )}

      {recording && (
        <div className="stage">
          <img src={earImg} alt="Ear" className="stage-icon" />
          <h2>వాయిస్ రికార్డింగ్ జరుగుతోంది….</h2>
          <p>Voice recording is in progress….</p>

          <div className="transcript-display">
            <div className="language-section telugu-section">
              <h3>Live Transcription (Telugu):</h3>
              <p className="transcript-text">
                {fullTelugu}
                <span className="partial-text">{partialTelugu}</span>
              </p>
            </div>
            <div className="language-section english-section">
              <h3>Live Translation (English):</h3>
              <p className="transcript-text">
                {fullEnglish}
                <span className="partial-text">{partialEnglish}</span>
              </p>
            </div>
          </div>

          <button className="stop-btn" onClick={stopRecording}>
            Stop
          </button>
          <button className="back-btn" onClick={retakeRecording}>
            Retake
          </button>
        </div>
      )}

      {audioURL && !recording && (
        <div className="playback stage">
          <h3>Preview Recording:</h3>
          <audio src={audioURL} controls className="audio-player" />

          <div className="transcript-display">
            <div className="language-section telugu-section">
              <h3>Transcription (Telugu):</h3>
              <p className="transcript-text">{fullTelugu}</p>
            </div>
            <div className="language-section english-section">
              <h3>Translation (English):</h3>
              <p className="transcript-text">{fullEnglish}</p>
            </div>
          </div>

          <div className="retake-section">
            <button className="back-btn" onClick={retakeRecording}>
              Retake
            </button>
          </div>

          <div className="submit-section">
            <button className="submit-btn" onClick={submitRecording}>
              Submit
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

